import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { RootState } from '../../store';
import { Company, Task, Branch, UserRole } from '../../types';
import Autocomplete from '@mui/material/Autocomplete';

const validationSchema = Yup.object({
  name: Yup.string().required('El nombre es requerido'),
  address: Yup.string().required('La dirección es requerida'),
  notes: Yup.string()
});

const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3000';

const Companies: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openTasksDialog, setOpenTasksDialog] = useState(false);
  const [companyTasks, setCompanyTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskFilter, setTaskFilter] = useState<'PENDING' | 'COMPLETED'>('PENDING');
  const [openTaskDetail, setOpenTaskDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchCompany, setSearchCompany] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const filteredCompanies = Array.isArray(companies) ? companies.filter(company => {
    if (branchFilter && company.branchId !== branchFilter) return false;
    return company.name.toLowerCase().includes(searchCompany.toLowerCase()) ||
           company.address.toLowerCase().includes(searchCompany.toLowerCase());
  }) : [];
  useEffect(() => {
    fetchCompanies();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axiosInstance.get('/branches');
      setBranches(response.data);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/companies');
      if (response.data && Array.isArray(response.data)) {
        setCompanies(response.data);
        setError(null);
      } else {
        console.error('Invalid response format:', response.data);
        setError('Formato de respuesta inválido');
        setCompanies([]);
      }
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      setError(error.message || 'Error al cargar las empresas');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (company?: Company) => {
    setSelectedCompany(company || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCompany(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (selectedCompany) {
        await axiosInstance.put(`/companies/${selectedCompany.id}`, values);
      } else {
        await axiosInstance.post('/companies', values);
      }
      fetchCompanies();
      handleCloseDialog();
      setError(null);
    } catch (error) {
      console.error('Error saving company:', error);
      setError('Error al guardar la empresa');
    }
  };

  const handleDelete = async (companyId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta empresa?')) {
      try {
        await axiosInstance.delete(`/companies/${companyId}`);
        fetchCompanies();
        setError(null);
      } catch (error) {
        console.error('Error deleting company:', error);
        setError('Error al eliminar la empresa');
      }
    }
  };

  const handleOpenTasksDialog = async (company: Company) => {
    setSelectedCompany(company);
    setOpenTasksDialog(true);
    setLoadingTasks(true);
    try {
      const response = await axiosInstance.get(`/tasks?companyId=${company.id}`);
      setCompanyTasks(response.data.filter((t: Task) => 
        taskFilter === 'COMPLETED' ? t.status === 'COMPLETED' : t.status !== 'COMPLETED'
      ));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setCompanyTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleToggleTasks = async () => {
    setTaskFilter(taskFilter === 'PENDING' ? 'COMPLETED' : 'PENDING');
    setLoadingTasks(true);
    try {
      const response = await axiosInstance.get(`/tasks?companyId=${selectedCompany?.id}`);
      setCompanyTasks(response.data.filter((t: Task) => 
        taskFilter === 'PENDING' ? t.status === 'COMPLETED' : t.status !== 'COMPLETED'
      ));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setCompanyTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleTaskRowClick = (task: Task) => {
    setSelectedTask(task);
    setOpenTaskDetail(true);
  };

  const handleCloseTaskDetail = () => {
    setOpenTaskDetail(false);
    setSelectedTask(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }



  return (
    <Box sx={{ width: '100%', p: 0, m: 0 }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 2 : 0}
      >
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
        >
          Empresas
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={isMobile}
        >
          Nueva Empresa
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={(user?.role === UserRole.MANAGER || user?.role === UserRole.DIRECTOR) ? 8 : 12}>
            <TextField
              fullWidth
              label="Buscar empresas"
              value={searchCompany}
              onChange={(e) => setSearchCompany(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          {(user?.role === UserRole.MANAGER || user?.role === UserRole.DIRECTOR) && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Sucursal</InputLabel>
                <Select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  label="Sucursal"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {filteredCompanies.map((company) => (
          <Grid item xs={12} sm={6} md={4} key={company.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    fontWeight: 600
                  }}
                >
                  {company.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    mb: 2
                  }}
                >
                  {company.address}
                </Typography>
                {company.notes && (
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      mb: 2,
                      fontStyle: 'italic'
                    }}
                  >
                    {company.notes}
                  </Typography>
                )}
                {company.branch && (
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      mb: 2,
                      fontWeight: 'bold'
                    }}
                  >
                    Sucursal: {company.branch.name}
                  </Typography>
                )}
                <Stack 
                  direction={isMobile ? "column" : "row"} 
                  spacing={1}
                  sx={{ mt: 'auto' }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenTasksDialog(company)}
                    fullWidth={isMobile}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    Ver Tareas
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenDialog(company)}
                    fullWidth={isMobile}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => handleDelete(company.id)}
                    fullWidth={isMobile}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    Eliminar
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCompany ? 'Editar Empresa' : 'Nueva Empresa'}
        </DialogTitle>
        <Formik
          initialValues={
            selectedCompany || {
              name: '',
              address: '',
              notes: '',
              branchId: ''
            }
          }
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="name"
                  label="Nombre"
                  autoComplete="off"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="address"
                  label="Dirección"
                  autoComplete="off"
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address && errors.address}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="notes"
                  label="Notas"
                  multiline
                  rows={4}
                  error={touched.notes && Boolean(errors.notes)}
                  helperText={touched.notes && errors.notes}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel id="branch-select-label">Sucursal</InputLabel>
                  <Field
                    as={Select}
                    labelId="branch-select-label"
                    name="branchId"
                    label="Sucursal"
                  >
                    <MenuItem value="">Ninguna</MenuItem>
                    {branches.map(branch => (
                      <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                    ))}
                  </Field>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancelar</Button>
                <Button type="submit" variant="contained" color="primary">
                  Guardar
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      <Dialog open={openTasksDialog} onClose={() => setOpenTasksDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Tareas de {selectedCompany?.name}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {taskFilter === 'COMPLETED' ? 'Tareas realizadas' : 'Tareas en curso'}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleToggleTasks}
              sx={{ ml: 2 }}
            >
              {taskFilter === 'PENDING' ? 'Ver tareas realizadas' : 'Ver tareas en curso'}
            </Button>
          </Box>
          {loadingTasks ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre de Servicio</TableCell>
                  <TableCell>Fecha y Hora</TableCell>
                  <TableCell>Responsable</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companyTasks.map((task) => (
                  <TableRow key={task.id} style={{ cursor: 'pointer' }} onClick={() => handleTaskRowClick(task)}>
                    <TableCell>{task.location}</TableCell>
                    <TableCell>{new Date(task.dateTime).toLocaleString()}</TableCell>
                    <TableCell>
                      {task.responsible ? `${task.responsible.firstName} ${task.responsible.lastName}` : 'No asignado'}
                    </TableCell>
                    <TableCell>{task.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTasksDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openTaskDetail} onClose={handleCloseTaskDetail} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle de la tarea</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box>
              <Typography><b>Nombre de Servicio:</b> {selectedTask.location}</Typography>
              <Typography><b>Fecha y hora:</b> {new Date(selectedTask.dateTime).toLocaleString()}</Typography>
              <Typography><b>Estado:</b> {selectedTask.status}</Typography>
              <Typography><b>Responsable:</b> {selectedTask.responsible ? `${selectedTask.responsible.firstName} ${selectedTask.responsible.lastName}` : 'No asignado'}</Typography>
              {selectedTask.notes && <Typography><b>Notas:</b> {selectedTask.notes}</Typography>}
              <Box mt={2}>
                {selectedTask.beforePhoto && selectedTask.beforePhoto.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Foto antes:</Typography>
                    {selectedTask.beforePhoto.map((url, idx) => (
                      <img key={idx} src={url.startsWith('http') ? url : `${backendUrl}${url.startsWith('/') ? url : `/uploads/${url}`}`} alt="Antes" style={{ maxWidth: '100%', marginBottom: 8 }} />
                    ))}
                  </Box>
                )}
                {selectedTask.duringPhoto && selectedTask.duringPhoto.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Foto durante:</Typography>
                    {selectedTask.duringPhoto.map((url, idx) => (
                      <img key={idx} src={url.startsWith('http') ? url : `${backendUrl}${url.startsWith('/') ? url : `/uploads/${url}`}`} alt="Durante" style={{ maxWidth: '100%', marginBottom: 8 }} />
                    ))}
                  </Box>
                )}
                {selectedTask.afterPhoto && selectedTask.afterPhoto.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Foto después:</Typography>
                    {selectedTask.afterPhoto.map((url, idx) => (
                      <img key={idx} src={url.startsWith('http') ? url : `${backendUrl}${url.startsWith('/') ? url : `/uploads/${url}`}`} alt="Después" style={{ maxWidth: '100%', marginBottom: 8 }} />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskDetail}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies; 